import React, { useEffect, useRef, useState } from 'react';
import '../pages/GalleryWeekend.css';

const STRAPI_URL = process.env.REACT_APP_STRAPI_API_URL;

// Dauer des Ausblendens; muss zur Transition von .gw-detail-overlay--closing
// in GalleryWeekend.css passen
const FADE_OUT_MS = 500;

function getNowImageUrl(entry) {
  const formats = entry.image?.formats;
  if (!formats && !entry.image?.url) return null;
  return STRAPI_URL + (formats?.large?.url || formats?.medium?.url || entry.image?.url);
}

// Im Feld "image" darf in Strapi auch ein Video liegen
export function isNowVideo(entry) {
  return Boolean(entry?.image?.mime?.startsWith('video/'));
}

export function getNowVideoUrl(entry) {
  return entry?.image?.url ? STRAPI_URL + entry.image.url : null;
}

// Wiederverwendbares Overlay fuer NOW-Beitraege mit Vor/Zurueck-Navigation
// (Pfeile + Touch-Swipe). Wird sowohl beim ersten Kartenbesuch automatisch
// gezeigt (Map.js) als auch per Klick auf den Event-Knopf in der NavigationBar.
//
// Props:
//   entries: Array der Beitraege, neueste zuerst (index 0 = neuester)
//   startIndex: mit welchem Beitrag geoeffnet wird (default 0)
//   onClose: Callback zum Schliessen
//   lightBackdrop: Hintergrund kaum abdunkeln, damit das Bild dahinter
//     erkennbar bleibt (Startseite)
//   showArchiveLink: Link zum NOW-Archiv anbieten (default true)
//   onVideoEnded: Callback, wenn ein Video zu Ende gelaufen ist oder nicht
//     geladen werden konnte
//   closing: auf true setzen, um das Overlay von aussen auszublenden; danach
//     wird onClose aufgerufen
//   fadeInMs: Dauer des Einblendens (default 800, siehe GalleryWeekend.css)
const NowOverlay = ({
  entries,
  startIndex = 0,
  onClose,
  lightBackdrop = false,
  showArchiveLink = true,
  onVideoEnded,
  closing = false,
  fadeInMs,
}) => {
  const [index, setIndex] = useState(startIndex);
  // true, waehrend das Overlay ausblendet; erst danach wird onClose aufgerufen
  const [fadingOut, setFadingOut] = useState(false);
  const touchStartX = useRef(null);
  // Eltern uebergeben onClose meist als neue Funktion pro Render; ueber die
  // Ref startet das den Ausblend-Timer nicht jedes Mal neu
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const isOpen = Array.isArray(entries) && entries.length > 0;
  const entry = isOpen ? entries[Math.min(index, entries.length - 1)] : null;
  const canGoNewer = index > 0;
  const canGoOlder = isOpen && index < entries.length - 1;

  // Bei jedem Oeffnen (neue entries-Liste) auf den gewuenschten Startpunkt springen
  useEffect(() => {
    setIndex(startIndex);
  }, [entries, startIndex]);

  useEffect(() => {
    if (closing) setFadingOut(true);
  }, [closing]);

  useEffect(() => {
    if (!fadingOut) return;
    const timer = setTimeout(() => {
      setFadingOut(false);
      onCloseRef.current();
    }, FADE_OUT_MS);
    return () => clearTimeout(timer);
  }, [fadingOut]);

  const requestClose = () => setFadingOut(true);

  useEffect(() => {
    if (!entry) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setFadingOut(true);
      if (e.key === 'ArrowRight' && canGoOlder) setIndex(i => i + 1);
      if (e.key === 'ArrowLeft' && canGoNewer) setIndex(i => i - 1);
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [entry, canGoOlder, canGoNewer]);

  if (!entry) return null;

  const goOlder = () => canGoOlder && setIndex(i => i + 1);
  const goNewer = () => canGoNewer && setIndex(i => i - 1);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(deltaX) < 40) return;
    if (deltaX < 0) {
      // nach links gewischt -> naechster (aelterer) Beitrag
      goOlder();
    } else {
      // nach rechts gewischt -> zurueck zum neueren Beitrag
      goNewer();
    }
  };

  return (
    <div
      className={
        'gw-detail-overlay'
        + (lightBackdrop ? ' gw-detail-overlay--light' : '')
        + (fadingOut ? ' gw-detail-overlay--closing' : '')
      }
      style={fadeInMs ? { '--gw-fade-in': `${fadeInMs}ms` } : undefined}
      onClick={(e) => { if (e.target === e.currentTarget) requestClose(); }}
    >
      <div
        className="gw-detail-panel"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <button
          className="gw-detail-close"
          onClick={requestClose}
          aria-label="Schliessen"
        >
          ✕
        </button>
        <span className="gw-now-badge">NOW</span>
        {entry.caption && (
          <h2 className="gw-detail-headline">{entry.caption}</h2>
        )}
        {entry.image && isNowVideo(entry) && (
          // Autoplay erlauben Browser nur stumm; der Ton laesst sich ueber
          // die Bedienelemente einschalten
          <video
            key={entry.id}
            src={getNowVideoUrl(entry)}
            className="gw-detail-img"
            controls
            autoPlay
            muted
            playsInline
            onEnded={onVideoEnded}
            onError={onVideoEnded}
          />
        )}
        {entry.image && !isNowVideo(entry) && (
          <img
            src={getNowImageUrl(entry)}
            alt={entry.caption || 'NOW'}
            className="gw-detail-img"
          />
        )}
        {entry.credit && (
          <p className="gw-detail-credit">{entry.credit}</p>
        )}

        {(entries.length > 1 || showArchiveLink) && (
          <div className={'gw-now-nav' + (entries.length > 1 ? '' : ' gw-now-nav--single')}>
            {entries.length > 1 && (
              <button
                type="button"
                className="gw-now-nav-arrow"
                onClick={goNewer}
                disabled={!canGoNewer}
                aria-label="Neuerer Beitrag"
              >
                ← Neuer
              </button>
            )}
            {/* Mitte: Link zum Archiv, sonst die Position im Archiv */}
            {showArchiveLink ? (
              <a href="/gallery-weekend" className="gw-now-archive-button">
                Zum NOW-Archiv →
              </a>
            ) : (
              <span className="gw-now-nav-count">{index + 1} / {entries.length}</span>
            )}
            {entries.length > 1 && (
              <button
                type="button"
                className="gw-now-nav-arrow"
                onClick={goOlder}
                disabled={!canGoOlder}
                aria-label="Älterer Beitrag"
              >
                Älter →
              </button>
            )}
          </div>
        )}

        {entry.body && (
          <div className="gw-detail-body">
            {entry.body.split('\n').map((para, i) =>
              para.trim() ? <p key={i}>{para}</p> : null
            )}
          </div>
        )}
        {entry.link && (
          <div className="gw-detail-link-row">
            <a
              href={entry.link}
              target="_blank"
              rel="noopener noreferrer"
              className="gw-now-nav-arrow"
            >
              Mehr erfahren →
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default NowOverlay;
